import { useState, useRef, useEffect } from "react";
import { useFieldScout } from "@/hooks/useFieldScout";
import { FieldScoutHUD } from "./FieldScoutHUD";
import { Button } from "@/components/ui/button";
import { X, StopCircle } from "lucide-react";
import { toast } from "sonner";
import { TradeType } from "@/lib/damageTypes";

interface FieldScoutProps {
  onComplete: (videoBlob: Blob, bookmarks: any[]) => void;
  onCancel: () => void;
}

export const FieldScout = ({ onComplete, onCancel }: FieldScoutProps) => {
  const {
    isRecording,
    duration,
    bookmarks,
    previewStream,
    startRecording,
    stopRecording,
    addBookmark,
    cancelRecording
  } = useFieldScout();

  const [selectedTrade, setSelectedTrade] = useState<TradeType>('roofing');
  const [selectedDamageType, setSelectedDamageType] = useState<string>('hail_damage');
  const [hasGPS, setHasGPS] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (!isRecording) {
      startRecording();
    }

    // Check GPS availability
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        () => setHasGPS(true),
        () => setHasGPS(false)
      );
    }

    return () => {
      cancelRecording();
    };
  }, []);

  useEffect(() => {
    if (videoRef.current && previewStream) {
      videoRef.current.srcObject = previewStream;
    }
  }, [previewStream]);

  const handleMarkDamage = () => {
    addBookmark(selectedTrade, selectedDamageType);
    
    // Visual feedback - flash effect
    const flash = document.createElement('div');
    flash.className = 'fixed inset-0 bg-white pointer-events-none z-50';
    flash.style.opacity = '0.8';
    document.body.appendChild(flash);
    
    setTimeout(() => {
      flash.style.transition = 'opacity 0.2s';
      flash.style.opacity = '0';
      setTimeout(() => document.body.removeChild(flash), 200);
    }, 50);

    // Haptic feedback
    if ('vibrate' in navigator) {
      navigator.vibrate(50);
    }
  };

  const handleStop = async () => {
    if (bookmarks.length === 0) {
      toast.error('Mark at least one damage spot before finishing');
      return;
    }

    try {
      const videoBlob = await stopRecording();
      onComplete(videoBlob, bookmarks);
    } catch (error) {
      console.error('Error stopping recording:', error);
      toast.error('Failed to save recording');
    }
  };

  const handleCancel = () => {
    cancelRecording();
    onCancel();
  };

  return (
    <div className="fixed inset-0 bg-black z-50 flex flex-col">
      {/* Video Preview */}
      <div className="relative flex-1">
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          className="w-full h-full object-cover"
        />

        {/* Top Controls - Higher z-index than HUD */}
        <div className="absolute top-0 left-0 right-0 p-4 flex justify-between z-50 bg-gradient-to-b from-black/60 to-transparent">
          <Button
            variant="outline"
            size="icon"
            onClick={handleCancel}
            className="bg-black/70 border-white/30 text-white hover:bg-black/90 shadow-lg"
          >
            <X className="w-5 h-5" />
          </Button>

          <Button
            variant="destructive"
            onClick={handleStop}
            className="gap-2 shadow-lg"
          >
            <StopCircle className="w-5 h-5" />
            Finish Scout
          </Button>
        </div>

        {/* HUD Overlay - Lower z-index */}
        <FieldScoutHUD
          duration={duration}
          bookmarkCount={bookmarks.length}
          selectedTrade={selectedTrade}
          onTradeSelect={setSelectedTrade}
          selectedDamageType={selectedDamageType}
          onDamageTypeSelect={setSelectedDamageType}
          onMarkDamage={handleMarkDamage}
          hasGPS={hasGPS}
        />
      </div>
    </div>
  );
};
